import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  VStack,
  HStack,
  Text,
  Box,
  Divider,
  useToast,
  Badge,
  Textarea,
  useColorModeValue,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import { RiCoupon3Line } from 'react-icons/ri'
import { AiOutlineThunderbolt } from 'react-icons/ai'
import { useBulkGenerateVouchers } from 'queries/voucher.query'

export function BulkVoucherModal({ isOpen, onClose }) {
  const toast = useToast()
  const mutation = useBulkGenerateVouchers()

  const bg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const labelColor = useColorModeValue('gray.600', 'gray.300')
  const codeBoxBg = useColorModeValue('gray.50', 'gray.900')
  const previewBg = useColorModeValue('purple.50', 'purple.900')

  const [form, setForm] = useState({
    count: 10,
    prefix: 'BAMYAN',
    discount_type: 'percent',
    discount_value: 10,
    max_uses: 1,
    valid_until: '',
    min_order_amount: '',
    event_id: '',
  })

  const [generatedCodes, setGeneratedCodes] = useState([])

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  const handleGenerate = async () => {
    try {
      const payload = {
        ...form,
        count: Number(form.count),
        discount_value: Number(form.discount_value),
        max_uses: Number(form.max_uses),
        min_order_amount: form.min_order_amount ? Number(form.min_order_amount) : undefined,
        event_id: form.event_id ? Number(form.event_id) : undefined,
        valid_until: form.valid_until || undefined,
      }

      const res = await mutation.mutateAsync(payload)

      const codes = res?.codes || []
      setGeneratedCodes(codes)

      toast({
        title: `${codes.length} vouchers generated successfully!`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      })
    } catch (err) {
      toast({
        title: 'Failed to generate vouchers',
        description: err?.response?.data?.message || err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleCopyCodes = () => {
    navigator.clipboard.writeText(generatedCodes.join('\n'))
    toast({ title: 'Codes copied to clipboard!', status: 'info', duration: 2000, isClosable: true })
  }

  const handleClose = () => {
    setGeneratedCodes([])
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent bg={bg} borderRadius="20px" border="1px solid" borderColor={borderColor}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box
              w="36px"
              h="36px"
              borderRadius="10px"
              bg="purple.500"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <AiOutlineThunderbolt color="white" size={18} />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="md" fontWeight="800">Bulk Generate Vouchers</Text>
              <Text fontSize="xs" color={labelColor} fontWeight="400">
                Create multiple promo codes at once
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton borderRadius="full" />

        <ModalBody>
          {generatedCodes.length > 0 ? (
            <VStack spacing={4} align="stretch">
              <Alert status="success" borderRadius="12px">
                <AlertIcon />
                <Text fontSize="sm" fontWeight="600">
                  {generatedCodes.length} vouchers created successfully!
                </Text>
              </Alert>

              <Box>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="700" color={labelColor}>Generated Codes</Text>
                  <Button size="xs" colorScheme="purple" variant="ghost" onClick={handleCopyCodes}>
                    Copy All
                  </Button>
                </HStack>
                <Box
                  bg={codeBoxBg}
                  borderRadius="12px"
                  border="1px solid"
                  borderColor={borderColor}
                  p={3}
                  maxH="200px"
                  overflowY="auto"
                >
                  <Textarea
                    value={generatedCodes.join('\n')}
                    readOnly
                    size="sm"
                    fontFamily="mono"
                    fontSize="xs"
                    border="none"
                    resize="none"
                    _focus={{ border: 'none', boxShadow: 'none' }}
                    minH="120px"
                  />
                </Box>
              </Box>
            </VStack>
          ) : (
            <VStack spacing={4} align="stretch">
              <HStack spacing={3}>
                <FormControl>
                  <FormLabel fontSize="xs" color={labelColor}>Code Prefix</FormLabel>
                  <Input
                    value={form.prefix}
                    onChange={(e) => handleChange('prefix', e.target.value.toUpperCase())}
                    placeholder="e.g. BAMYAN"
                    fontFamily="mono"
                    fontWeight="700"
                    fontSize="sm"
                    borderRadius="10px"
                    textTransform="uppercase"
                  />
                </FormControl>
                <FormControl maxW="120px">
                  <FormLabel fontSize="xs" color={labelColor}>Quantity</FormLabel>
                  <NumberInput
                    value={form.count}
                    onChange={(val) => handleChange('count', val)}
                    min={1}
                    max={500}
                    borderRadius="10px"
                  >
                    <NumberInputField borderRadius="10px" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </HStack>

              <Divider />

              <HStack spacing={3}>
                <FormControl>
                  <FormLabel fontSize="xs" color={labelColor}>Discount Type</FormLabel>
                  <Select
                    value={form.discount_type}
                    onChange={(e) => handleChange('discount_type', e.target.value)}
                    borderRadius="10px"
                    fontSize="sm"
                  >
                    <option value="percent">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (€)</option>
                  </Select>
                </FormControl>
                <FormControl maxW="140px">
                  <FormLabel fontSize="xs" color={labelColor}>
                    Value {form.discount_type === 'percent' ? '(%)' : '(€)'}
                  </FormLabel>
                  <NumberInput
                    value={form.discount_value}
                    onChange={(val) => handleChange('discount_value', val)}
                    min={0}
                    max={form.discount_type === 'percent' ? 100 : 9999}
                    borderRadius="10px"
                  >
                    <NumberInputField borderRadius="10px" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </HStack>

              <HStack spacing={3}>
                <FormControl>
                  <FormLabel fontSize="xs" color={labelColor}>Max Uses per Code</FormLabel>
                  <NumberInput
                    value={form.max_uses}
                    onChange={(val) => handleChange('max_uses', val)}
                    min={1}
                    borderRadius="10px"
                  >
                    <NumberInputField borderRadius="10px" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" color={labelColor}>Min. Order Amount (€)</FormLabel>
                  <Input
                    type="number"
                    value={form.min_order_amount}
                    onChange={(e) => handleChange('min_order_amount', e.target.value)}
                    placeholder="Optional"
                    borderRadius="10px"
                    fontSize="sm"
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel fontSize="xs" color={labelColor}>Expiry Date</FormLabel>
                <Input
                  type="date"
                  value={form.valid_until}
                  onChange={(e) => handleChange('valid_until', e.target.value)}
                  borderRadius="10px"
                  fontSize="sm"
                />
              </FormControl>

              <Box
                p={3}
                bg={previewBg}
                borderRadius="12px"
                border="1px dashed"
                borderColor="purple.300"
              >
                <Text fontSize="xs" fontWeight="600" color="purple.500" mb={1}>Preview</Text>
                <HStack spacing={2} flexWrap="wrap">
                  {Array.from({ length: Math.min(3, form.count) }).map((_, i) => (
                    <Badge
                      key={i}
                      colorScheme="purple"
                      fontFamily="mono"
                      fontSize="xs"
                      px={2}
                      py={1}
                      borderRadius="6px"
                    >
                      {form.prefix}-XXXXXX
                    </Badge>
                  ))}
                  {form.count > 3 && (
                    <Badge colorScheme="gray" fontSize="xs" px={2} py={1} borderRadius="6px">
                      +{form.count - 3} more
                    </Badge>
                  )}
                </HStack>
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="ghost" size="sm" onClick={handleClose} borderRadius="10px">
            {generatedCodes.length > 0 ? 'Close' : 'Cancel'}
          </Button>
          {generatedCodes.length === 0 && (
            <Button
              leftIcon={<AiOutlineThunderbolt />}
              colorScheme="purple"
              size="sm"
              borderRadius="10px"
              onClick={handleGenerate}
              isLoading={mutation.isPending}
              loadingText="Generating..."
            >
              Generate {form.count} Vouchers
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
